import Keycloak from 'keycloak-js';
import { config } from '@/config';
import { DEFAULT_LANGUAGE } from '@/features/language/config';
import { readLanguagePreference } from '@/features/language/preference';
import type { KeycloakConfig, TokenRefreshConfig } from '@/types';

/**
 * Default token refresh configuration
 */
export const DEFAULT_TOKEN_REFRESH_CONFIG: TokenRefreshConfig = {
  minValidity: 30, // Refresh 30s before expiry
  refreshInterval: 10000, // Check every 10s
  maxRetries: 3,
};

const KEYCLOAK_INIT_TIMEOUT_MS = 12000;
const THEME_PARAM = 'portal_theme';
const LANGUAGE_PARAM = 'kc_locale';
const THEME_STORAGE_KEY = 'shell-theme';

const isThemeMode = (value: string | null | undefined): value is 'light' | 'dark' =>
  value === 'light' || value === 'dark';

const resolveLoginTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') {
    return 'dark';
  }

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (isThemeMode(storedTheme)) {
    return storedTheme;
  }

  const attrTheme = document.documentElement.getAttribute('data-theme');
  if (isThemeMode(attrTheme)) {
    return attrTheme;
  }

  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
};

const resolveLoginLocale = (): 'de' | 'en' => {
  return readLanguagePreference() ?? DEFAULT_LANGUAGE;
};

const buildLoginUrl = async (keycloak: Keycloak): Promise<string> => {
  const locale = resolveLoginLocale();
  const loginUrl = new URL(await keycloak.createLoginUrl({ locale }), window.location.origin);
  loginUrl.searchParams.set('ui_locales', locale);
  loginUrl.searchParams.set(LANGUAGE_PARAM, locale);
  loginUrl.searchParams.set(THEME_PARAM, resolveLoginTheme());
  return loginUrl.toString();
};

/**
 * Keycloak Service class to handle Keycloak instance and operations
 */
class KeycloakService {
  private static instance: KeycloakService;
  private keycloak: Keycloak | null = null;
  private initialized: boolean = false;
  private tokenRefreshPromise: Promise<boolean> | null = null;

  private constructor() {}

  public static getInstance(): KeycloakService {
    if (!KeycloakService.instance) {
      KeycloakService.instance = new KeycloakService();
    }
    return KeycloakService.instance;
  }

  /**
   * Get the Keycloak instance
   */
  public getKeycloak(): Keycloak | null {
    return this.keycloak;
  }

  /**
   * Initialize Keycloak
   */
  public init(keycloakConfig?: Partial<KeycloakConfig>): Keycloak {
    if (this.keycloak) {
      return this.keycloak;
    }

    const appConfig = config();
    
    const keycloakOptions: Keycloak.KeycloakConfig = {
      url: keycloakConfig?.url ?? appConfig.keycloak.url,
      realm: keycloakConfig?.realm ?? appConfig.keycloak.realm,
      clientId: keycloakConfig?.clientId ?? appConfig.keycloak.clientId,
    };

    this.keycloak = new Keycloak(keycloakOptions);
    return this.keycloak;
  }

  /**
   * Initialize connection with Keycloak
   */
  public async connect(
    onTokenRefresh?: (token: string) => void,
    onTokenRefreshFailure?: () => void
  ): Promise<boolean> {
    if (!this.keycloak) {
      this.init();
    }

    if (this.initialized) {
      return !!this.keycloak?.authenticated;
    }

    try {
      if (!this.keycloak) throw new Error('Keycloak not initialized');
      let timeoutId: number | undefined;
      const initPromise = this.keycloak.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        checkLoginIframe: false,
        pkceMethod: 'S256',
        enableLogging: true
      });

      const timeoutPromise = new Promise<boolean>((_resolve, reject) => {
        timeoutId = window.setTimeout(() => {
          reject(new Error(`Keycloak init timed out after ${KEYCLOAK_INIT_TIMEOUT_MS}ms`));
        }, KEYCLOAK_INIT_TIMEOUT_MS);
      });

      let authenticated = false;
      try {
        authenticated = await Promise.race([initPromise, timeoutPromise]);
      } finally {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
      }

      this.initialized = true;

      if (authenticated && onTokenRefresh) {
        this.keycloak.onTokenExpired = () => {
          this.refreshToken(DEFAULT_TOKEN_REFRESH_CONFIG.minValidity)
            .then((refreshed) => {
              if (refreshed && this.keycloak?.token) {
                onTokenRefresh(this.keycloak.token);
              } else if (!this.hasValidToken(0)) {
                onTokenRefreshFailure?.();
              }
            })
            .catch(() => {
              console.error('Failed to refresh token via onTokenExpired');
              onTokenRefreshFailure?.();
            });
        };
      }

      return authenticated;
    } catch (error) {
      console.error('Failed to initialize Keycloak:', error);
      return false;
    }
  }

  /**
   * Trigger login
   */
  public async login(): Promise<void> {
    if (!this.keycloak) return;
    window.location.assign(await buildLoginUrl(this.keycloak));
  }

  /**
   * Trigger logout
   */
  public async logout(): Promise<void> {
    if (!this.keycloak) return;
    await this.keycloak.logout({
      redirectUri: window.location.origin,
    });
  }

  /**
   * Refresh token
   */
  public async refreshToken(minValidity: number = DEFAULT_TOKEN_REFRESH_CONFIG.minValidity): Promise<boolean> {
    if (!this.keycloak) return false;

    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }

    const refreshPromise = this.keycloak.updateToken(minValidity)
      .catch((error) => {
        console.error('Failed to refresh token:', error);
        return false;
      });

    this.tokenRefreshPromise = refreshPromise;
    try {
      return await refreshPromise;
    } finally {
      if (this.tokenRefreshPromise === refreshPromise) {
        this.tokenRefreshPromise = null;
      }
    }
  }

  /**
   * Get current token
   */
  public getToken(): string | undefined {
    return this.keycloak?.token;
  }

  /**
   * Returns whether the current token is still valid for at least minValidity seconds.
   */
  public hasValidToken(minValidity: number = 0): boolean {
    if (!this.keycloak?.token) {
      return false;
    }

    if (typeof this.keycloak.isTokenExpired === 'function') {
      return !this.keycloak.isTokenExpired(minValidity);
    }

    const expiry = this.keycloak.tokenParsed?.exp;
    if (!expiry) {
      return false;
    }

    return Date.now() < (expiry * 1000) - (minValidity * 1000);
  }

  /**
   * Ensure a usable token is available before handing auth context to embedded apps.
   */
  public async ensureValidToken(
    minValidity: number = DEFAULT_TOKEN_REFRESH_CONFIG.minValidity
  ): Promise<string | null> {
    if (!this.keycloak?.token) {
      return null;
    }

    if (minValidity >= 0 && this.hasValidToken(minValidity)) {
      return this.keycloak.token;
    }

    try {
      await this.refreshToken(minValidity);
    } catch {
      // refreshToken already records the failure; validity below decides the outcome.
    }

    return this.hasValidToken(0) ? this.keycloak.token ?? null : null;
  }

  /**
   * Get user profile
   */
  public async loadUserProfile(): Promise<Keycloak.KeycloakProfile | undefined> {
    if (!this.keycloak) return undefined;
    try {
      return await this.keycloak.loadUserProfile();
    } catch (error) {
      console.error('Failed to load user profile:', error);
      return undefined;
    }
  }

  /**
   * Get parsed token
   */
  public getTokenParsed(): Keycloak.KeycloakTokenParsed | undefined {
    return this.keycloak?.tokenParsed;
  }
}

export const keycloakService = KeycloakService.getInstance();
export { buildLoginUrl };
export default keycloakService;
